import { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner, faUser, faPhone, faTruck } from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors.jsx';
import { useTranslation } from 'react-i18next';

const fmt = (val) => {
    const n = parseFloat(val);
    if (!val || isNaN(n)) return '—';
    return n.toLocaleString('en-IN', { maximumFractionDigits: 2 });
};

const StatusBadge = ({ status }) => {
    const { t } = useTranslation();
    const STATUS_META = {
        supervisor_assigned: { label: t('supervisorAnalytics.workloadDetail.statusAssigned'), bg: '#ede9fe', text: '#6d28d9' },
        loading:             { label: t('supervisorAnalytics.workloadDetail.statusLoading'),  bg: '#dbeafe', text: '#1d4ed8' },
        loaded:              { label: t('supervisorAnalytics.workloadDetail.statusLoaded'),   bg: '#d1fae5', text: '#065f46' },
        pending:             { label: t('supervisorAnalytics.workloadDetail.statusPending'),  bg: '#fef3c7', text: '#92400e' },
        in_transit:          { label: t('supervisorAnalytics.workloadDetail.statusInTransit'), bg: '#e0f2fe', text: '#0369a1' },
        delivered:           { label: t('supervisorAnalytics.workloadDetail.statusDelivered'),  bg: '#f0fdf4', text: '#15803d' },
        SUPERVISOR_ASSIGNED: { label: t('supervisorAnalytics.workloadDetail.statusAssigned'), bg: '#ede9fe', text: '#6d28d9' },
        LOADING:             { label: t('supervisorAnalytics.workloadDetail.statusLoading'),  bg: '#dbeafe', text: '#1d4ed8' },
        LOADED:              { label: t('supervisorAnalytics.workloadDetail.statusLoaded'),   bg: '#d1fae5', text: '#065f46' },
        PENDING:             { label: t('supervisorAnalytics.workloadDetail.statusPending'),  bg: '#fef3c7', text: '#92400e' },
    };
    const m = STATUS_META[status] || { label: status ?? '—', bg: '#f3f4f6', text: '#374151' };
    return (
        <span className="px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap flex-shrink-0"
            style={{ backgroundColor: m.bg, color: m.text }}>
            {m.label}
        </span>
    );
};

const TileCount = ({ label, count, color }) => (
    <div className="flex flex-col items-center px-3 py-2 rounded-xl flex-1"
        style={{ backgroundColor: color + '15', border: `1px solid ${color}30` }}>
        <span className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color }}>{label}</span>
        <span className="text-xl font-black leading-none" style={{ color }}>{count ?? 0}</span>
    </div>
);

const DetailContent = ({ row, detail, loading }) => {
    const { t } = useTranslation();
    const data = detail ?? row;
    const subOrders = detail?.sub_orders ?? [];

    if (loading && !detail) {
        return (
            <div className="flex items-center justify-center py-16 gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                <span className="text-sm">{t('supervisorAnalytics.workloadDetail.loading')}</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Status tiles */}
            <div className="flex items-stretch gap-2">
                <TileCount label={t('supervisorAnalytics.workloadDetail.tileAssigned')} count={data.assigned_count} color="#6366f1" />
                <TileCount label={t('supervisorAnalytics.workloadDetail.tileLoading')}  count={data.loading_count}  color="#3b82f6" />
                <TileCount label={t('supervisorAnalytics.workloadDetail.tileLoaded')}   count={data.loaded_count}   color="#10b981" />
            </div>

            {/* Weight tiles */}
            <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-xl border" style={{ borderColor: '#fcd34d', backgroundColor: '#fffbeb' }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#92400e' }}>{t('supervisorAnalytics.workloadDetail.pendingLoadWt')}</p>
                    <p className="text-lg font-black" style={{ color: '#d97706' }}>
                        {fmt(data.pending_loading_weight)} <span className="text-xs font-semibold">{t('supervisorAnalytics.workloadDetail.kg')}</span>
                    </p>
                </div>
                <div className="p-3 rounded-xl border" style={{ borderColor: '#6ee7b7', backgroundColor: '#ecfdf5' }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-0.5" style={{ color: '#065f46' }}>{t('supervisorAnalytics.workloadDetail.alreadyLoaded')}</p>
                    <p className="text-lg font-black" style={{ color: '#059669' }}>
                        {fmt(data.loaded_weight)} <span className="text-xs font-semibold">{t('supervisorAnalytics.workloadDetail.kg')}</span>
                    </p>
                </div>
            </div>

            {/* Sub-orders — compact single-line rows */}
            <div>
                <p className="text-[10px] font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: UI_COLORS.TEXT.MUTED }}>
                    <FontAwesomeIcon icon={faTruck} className="text-[10px]" />
                    {t('supervisorAnalytics.workloadDetail.subOrders', { count: String(data.pending_sub_orders ?? subOrders.length) })}
                </p>

                {loading ? (
                    <div className="flex items-center justify-center py-6 gap-2" style={{ color: UI_COLORS.TEXT.MUTED }}>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin text-sm" />
                    </div>
                ) : subOrders.length === 0 ? (
                    <p className="text-xs text-center py-4" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('supervisorAnalytics.workloadDetail.noSubOrders')}</p>
                ) : (
                    <div className="rounded-xl border overflow-hidden" style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                        {subOrders.map((so, idx) => (
                            <div
                                key={so.sub_order_id ?? idx}
                                className="flex items-center gap-3 px-3 py-2.5 text-xs"
                                style={{
                                    borderTop: idx > 0 ? `1px solid ${UI_COLORS.BORDERS.LIGHT}` : undefined,
                                    backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa',
                                }}
                            >
                                {/* Sub-order number */}
                                <span className="font-bold flex-shrink-0 min-w-[130px]" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                    {so.sub_order_number}
                                </span>

                                {/* Customer */}
                                <span className="flex-1 truncate" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    {so.tally_name || so.customer_name}
                                </span>

                                {/* Location */}
                                <span className="hidden sm:block truncate max-w-[100px]" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    {so.location_name ?? '—'}
                                </span>

                                {/* Vehicle */}
                                <span className="hidden md:block font-mono flex-shrink-0" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                                    {so.vehicle_number ?? '—'}
                                </span>

                                {/* Weight */}
                                <span className="font-semibold flex-shrink-0 text-right min-w-[70px]" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                    {fmt(so.assigned_weight)} {so.unit_type ?? t('supervisorAnalytics.workloadDetail.kg')}
                                </span>

                                {/* Status */}
                                <StatusBadge status={so.status} />
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

// inline=true  → renders content directly (supervisor's own view, no modal)
// inline=false → renders as modal (admin/sales drill-in)
const SupervisorWorkloadDetail = ({ row, detail, loading, onClose, inline = false }) => {
    useEffect(() => {
        if (inline) return;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, inline]);

    if (!row) return null;

    if (inline) {
        return <DetailContent row={row} detail={detail} loading={loading} />;
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.OVERLAY }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full sm:max-w-3xl bg-white sm:rounded-2xl shadow-2xl max-h-[90vh] flex flex-col"
                style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}>
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3.5 border-b flex-shrink-0"
                    style={{ borderColor: UI_COLORS.BORDERS.LIGHT }}>
                    <div className="flex items-center gap-2.5">
                        <div className="h-8 w-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: UI_COLORS.BRAND.PRIMARY }}>
                            <FontAwesomeIcon icon={faUser} />
                        </div>
                        <div>
                            <p className="font-bold text-sm leading-tight" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                                {row.supervisor_name}
                            </p>
                            {row.supervisor_mobile && (
                                <p className="text-[11px] flex items-center gap-1" style={{ color: UI_COLORS.TEXT.MUTED }}>
                                    <FontAwesomeIcon icon={faPhone} className="text-[9px]" />
                                    {row.supervisor_mobile}
                                </p>
                            )}
                        </div>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg"
                        style={{ color: UI_COLORS.TEXT.MUTED, backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.MUTED, 0.08) }}>
                        <FontAwesomeIcon icon={faTimes} className="text-sm" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto px-5 py-4">
                    <DetailContent row={row} detail={detail} loading={loading} />
                </div>
            </div>
        </div>
    );
};

export default SupervisorWorkloadDetail;
